import React, { useState, useEffect, FocusEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Button, Collapse } from "react-bootstrap";
import { ActionHandlerFunction } from "@preveil-api";
import {
  DriveUIActionTypes, CheckboxStates, GenericEntryEntity, DriveEntryType, getEntityIcon, buildNavigationLink, DriveConstants, useGetPath, Account, useGetPathList,
  PathInfo
} from "src/common";
import { Checkbox, Icon, Loading } from "src/components";
import { Breadcrumbs } from "..";

type AllProps = {
  current_account: Account;
  entry: GenericEntryEntity;
  selected: boolean;
  handleAction: ActionHandlerFunction;
}

// Description: This component displays a single row in the list view of starred entries.
function ListRowComponent(props: AllProps) {
  const { current_account, entry, selected, handleAction } = props;
  const navigate = useNavigate();
  const [opened, setOpened] = useState<boolean>(false);
  const [_path_info, setPathInfo] = useState<PathInfo[] | undefined>();
  const [_selected, setSelected] = useState<boolean>(selected);
  const { getPath, full_path_list } = useGetPath(current_account);
  const { buildBreadcrumbs, breadcrumbs_paths } = useGetPathList(current_account);

  useEffect(() => {
    setSelected(selected);
  }, [selected]);

  useEffect(() => {
    (!!entry) && opened &&
      getPath(entry.collection_id, entry.id);
  }, [opened]);

  // Description: Fetch the item full path list if not already resolved into PathInfo
  useEffect(() => {
    (!!full_path_list) &&
      buildBreadcrumbs(full_path_list, undefined, entry.type === DriveEntryType.LINK);
  }, [full_path_list]);

  // Description: Upon fully decrypted, update the main Entry in ListView
  useEffect(() => {
    !!breadcrumbs_paths && handleSuccessBreadcrumbs(breadcrumbs_paths.slice());
  }, [breadcrumbs_paths]);

  // Description: Handle the successful return from getPathInfo and send to parent for storing
  function handleSuccessBreadcrumbs(_breadcrumbs_paths: PathInfo[]) {
    setPathInfo(_breadcrumbs_paths);
  }

  // Description: Handle the click of checkbox and changes of checkbox state 
  function handleCheckboxChange(e: FocusEvent<HTMLInputElement>) {
    const new_selected = !_selected;
    setSelected(new_selected);
    handleAction({ actionType: DriveUIActionTypes.Select, params: { selected: e.target.checked, entry } });
  }

  // Description: build the url and navigate 
  function handleRowClick(_entry: GenericEntryEntity) {
    const is_acl_node = !!_entry.parent_id && _entry.type === DriveEntryType.ACL_NODE;
    const url = (_entry.type === DriveEntryType.FILE && !!_entry.parent_id) ?
      buildNavigationLink(_entry.collection_id, _entry.parent_id, _entry.type, _entry.id) :
      _entry.type !== DriveEntryType.FILE &&
      buildNavigationLink(_entry.collection_id, is_acl_node ? DriveConstants.DIRECT_LINK_MASK_ID : entry.id, entry.type);
    !!url && navigate(url);
  }

  return <>
    <Row onClick={() => handleRowClick(entry)} className={`${selected ? "active " : opened ? "border-bottom-0" : ""}`}>
      <Col md="1"> <Checkbox className="sr-only"
        onChange={(e: FocusEvent<HTMLInputElement>) => { handleCheckboxChange(e); }}
        selected={_selected ? CheckboxStates.checked : CheckboxStates.empty}
        value="all" label="Select All" disabled={false} /></Col>
      <Col md="5" className="entry-name col">
        <span data-tooltip-content={!!entry.is_deleted ? "This item was deleted from My PreVeil." : ""}
          data-tooltip-id="pv-tooltip"
          data-tooltip-place="top">
          <Button variant="icon" className="drive-starred" size="sm" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpened(false); handleAction({ actionType: DriveUIActionTypes.Unstar, params: [entry.id] }); }}>
            <Icon className="pv-icon-starred" tooltip="Remove Starred Status" />
          </Button>
          <Icon className={(!!entry.is_deleted && entry.type !== DriveEntryType.FILE) ? "ficon-folder" : (entry.type_class || getEntityIcon(entry.type))} />
          {entry.name}
        </span>
      </Col>
      <Col md="2" className="order-3">{entry.type_label}</Col>
      <Col md="3" className="order-4">{entry.last_modification_date}</Col>
      <Col md="1" className="order-md-5 order-2">
        <Button variant="icon" size="sm" className="path-chevron" onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpened(!opened); }} aria-label="View Path">
          <Icon className={opened ? "ficon-chevron-up" : "ficon-chevron-down"} tooltip={opened ? "Hide Path" : "View Path"} />
        </Button>
      </Col>
    </Row>
    <Collapse in={opened}>
      <div>
        <div className="collapsible-path">
          {
            !!_path_info ?
              <Breadcrumbs
                entry={entry}
                path_info={_path_info} /> :
              <Loading className="in-place" noLogo={true} />
          }
        </div>
      </div>
    </Collapse>
  </>;
}

export default React.memo(ListRowComponent);

